import React from 'react';
import { Helmet } from 'react-helmet-async';
import CtaStrip from '../components/CtaStrip';
import Footer from '../components/Footer';

const steps = [
  {
    num: '01',
    title: 'Send Your Requirement',
    desc: 'Fill in the contact form or email directly. Describe what you need — a plugin, a flow, an integration, a chatbot, or just a problem you need solved. No need for a formal spec.',
    icon: '📋',
  },
  {
    num: '02',
    title: 'Discovery Call (30 min)',
    desc: 'We jump on a call to walk through the requirement in detail. I ask the right questions about your D365 environment, your business logic, and your constraints. You get to assess whether I\'m the right fit.',
    icon: '📞',
  },
  {
    num: '03',
    title: 'Fixed Scope & Price',
    desc: 'Within 24 hours of the discovery call, you receive a written scope document with exact deliverables, a fixed price, and a realistic timeline. No vague estimates, no hidden charges.',
    icon: '📄',
  },
  {
    num: '04',
    title: 'Development & Updates',
    desc: 'Work begins immediately on confirmation. I build iteratively and share progress updates at agreed milestones. You have direct access to me throughout — no account manager in between.',
    icon: '⚙️',
  },
  {
    num: '05',
    title: 'Testing & Handover',
    desc: 'Everything is tested in your sandbox before production deployment. Handover includes a managed solution file, documentation of what was built, and a post-go-live support window.',
    icon: '✅',
  },
  {
    num: '06',
    title: 'Ongoing Support',
    desc: 'After go-live, I\'m available for questions, tweaks, and follow-on work. Many clients move to a monthly support retainer for peace of mind.',
    icon: '🛡️',
  },
];

const pricing = [
  {
    label: 'Small Task',
    range: 'From $150',
    desc: 'Quick fixes, minor form scripts, small flow adjustments, or consultation calls.',
    examples: ['Bug fix or error investigation', 'Simple Power Automate flow', 'Field or view customisation', 'Plugin registration issue'],
    accent: 'var(--ms-teal)',
    bg: '#DFF6F0',
  },
  {
    label: 'Standard Project',
    range: '$400 – $1,200',
    desc: 'Most common engagement type. A clearly scoped plugin, PCF control, chatbot topic, or integration.',
    examples: ['Custom C# plugin with business logic', 'PVA chatbot with D365 integration', 'PCF field control', 'Power Automate approval flow'],
    accent: 'var(--ms-blue)',
    bg: 'var(--ms-blue-pale)',
    featured: true,
  },
  {
    label: 'Complex Project',
    range: '$1,500 – $5,000+',
    desc: 'Multi-component builds, full integrations, data migrations, or ongoing retainer arrangements.',
    examples: ['Full third-party system integration', 'Data migration from legacy CRM', 'Multi-topic PVA chatbot deployment', 'Monthly support retainer'],
    accent: 'var(--ms-purple)',
    bg: '#F4F0FF',
  },
];

const faqs = [
  { q: 'Do you charge by the hour or fixed price?', a: 'Fixed price, always. After the discovery call I provide a written scope with a single price for the agreed deliverables. If requirements change mid-project, we agree a change order before any additional work begins.' },
  { q: 'What if the project runs over scope?', a: 'It won\'t — because scope is agreed in writing before work begins. If you want to add something new during the project, I raise a change order with the additional cost and timeline. You decide whether to proceed.' },
  { q: 'Do you work with clients outside your time zone?', a: 'Yes. All of my current clients are in the UK, Europe, the US, and Australia. We work asynchronously with scheduled calls at mutually convenient times. Time zones have never been an obstacle.' },
  { q: 'What access do you need to my D365 environment?', a: 'Typically a non-production (sandbox) environment with System Customizer or System Administrator access. I never need production access until the deployment phase, and even then only for the duration of the deployment.' },
  { q: 'Can you sign an NDA?', a: 'Yes. I routinely sign NDAs before discovery calls for clients in regulated industries. Send me your standard NDA and I\'ll review and sign it within 24 hours.' },
];

export default function PricingPage({ onNavigate }) {
  return (
    <div>
      <Helmet>
        <title>How I Work & Pricing — Dynamics 365 Consultant | AdjustD365</title>
        <meta name="description" content="Transparent pricing and a clear process for every D365 project. Fixed-price quotes, no hidden charges, direct developer access. See how AdjustD365 works and what to expect." />
        <link rel="canonical" href="https://adjustd365.com/how-i-work" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': faqs.map(f => ({
            '@type': 'Question',
            'name': f.q,
            'acceptedAnswer': { '@type': 'Answer', 'text': f.a },
          })),
        })}</script>
      </Helmet>

      {/* HERO */}
      <div className="section-pad" style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '56px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-s)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', color: 'var(--text-s)', cursor: 'pointer', fontSize: '13px' }}>Home</button>
            <span>›</span>
            <span style={{ color: 'var(--ms-blue)', fontWeight: 500 }}>How I Work</span>
          </div>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>Process & Pricing</div>
          <h1 style={{ fontSize: 'clamp(26px,3vw,42px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '12px' }}>
            How I Work & What It Costs
          </h1>
          <p style={{ fontSize: '16px', color: 'var(--text-m)', maxWidth: '580px', lineHeight: 1.7 }}>
            No surprises, no ambiguity. Here's exactly how every project runs from first contact to handover — and transparent pricing so you can plan your budget.
          </p>
        </div>
      </div>

      {/* PROCESS */}
      <section className="section-pad" style={{ padding: '80px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>The Process</div>
          <h2 style={{ fontSize: 'clamp(22px,2.5vw,32px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '48px' }}>
            From first message to go-live
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '24px' }} className="grid-3col">
            {steps.map((s, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '6px', padding: '28px', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '20px', right: '20px', fontSize: '11px', fontWeight: 700, color: 'var(--border-2)', letterSpacing: '0.04em' }}>{s.num}</div>
                <span style={{ fontSize: '28px', marginBottom: '16px', display: 'block' }}>{s.icon}</span>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-h)', marginBottom: '10px' }}>{s.title}</div>
                <p style={{ fontSize: '13px', color: 'var(--text-m)', lineHeight: 1.7, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="section-pad" style={{ padding: '80px 48px', background: 'var(--bg-2)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>Pricing</div>
          <h2 style={{ fontSize: 'clamp(22px,2.5vw,32px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '12px' }}>
            Transparent, fixed-price engagements
          </h2>
          <p style={{ fontSize: '15px', color: 'var(--text-m)', marginBottom: '44px', maxWidth: '560px' }}>
            Ranges below are indicative. Every project gets a fixed price after the discovery call — not an hourly rate that can spiral.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px' }} className="grid-3col">
            {pricing.map((p, i) => (
              <div key={i} style={{
                background: '#fff', border: `1px solid ${p.featured ? p.accent : 'var(--border)'}`,
                borderTop: `4px solid ${p.accent}`, borderRadius: '6px', padding: '32px',
                boxShadow: p.featured ? '0 4px 24px rgba(0,120,212,0.12)' : 'none',
                position: 'relative',
              }}>
                {p.featured && (
                  <div style={{ position: 'absolute', top: '-1px', right: '20px', background: p.accent, color: '#fff', fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '0 0 6px 6px' }}>
                    Most Common
                  </div>
                )}
                <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: p.accent, marginBottom: '8px' }}>{p.label}</div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-h)', letterSpacing: '-0.02em', marginBottom: '12px' }}>{p.range}</div>
                <p style={{ fontSize: '13px', color: 'var(--text-m)', lineHeight: 1.6, marginBottom: '20px' }}>{p.desc}</p>
                <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-s)', marginBottom: '10px' }}>Examples</div>
                <ul style={{ paddingLeft: '16px', margin: 0 }}>
                  {p.examples.map((e, j) => (
                    <li key={j} style={{ fontSize: '13px', color: 'var(--text-m)', lineHeight: 1.7, marginBottom: '4px' }}>{e}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-s)', marginTop: '24px', textAlign: 'center' }}>
            All prices in USD. GBP and EUR equivalents available on request. VAT not applicable.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" style={{ padding: '80px 48px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>FAQ</div>
          <h2 style={{ fontSize: 'clamp(22px,2.5vw,32px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '36px' }}>Common Questions</h2>
          <div>
            {faqs.map((f, i) => (
              <div key={i} style={{ padding: '24px 0', borderBottom: i < faqs.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-h)', marginBottom: '8px' }}>{f.q}</div>
                <p style={{ fontSize: '14px', color: 'var(--text-m)', lineHeight: 1.75, margin: 0 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaStrip
        heading="Ready to get a fixed-price quote?"
        subtext="Send me your requirement and I'll come back within 24 hours with a clear scope and price."
        buttonLabel="Get in Touch"
        onAction={() => onNavigate('contact')}
      />
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
